/**
 * 用户资料API函数
 * 提供获取、创建和更新用户资料等功能
 */
import { supabase } from '../supabaseClient';

/**
 * 生成通用UUID，兼容所有浏览器
 * @returns {string} 生成的UUID
 */
const generateUUID = () => {
  // 检查环境是否支持crypto.randomUUID
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  
  // 兼容方法：使用Math.random生成UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * 生成体验用户ID
 * @returns {string} 体验用户ID
 */
const generateExperienceUserId = () => {
  // 检查是否已经有一个保存的体验用户ID
  const savedId = localStorage.getItem('experience_user_id');
  if (savedId) {
    return savedId;
  }
  
  // 生成新的随机ID，前缀为exp-，确保与真实用户ID区分开
  const randomId = `exp-${generateUUID()}`;
  // 保存到本地存储
  localStorage.setItem('experience_user_id', randomId);
  return randomId;
};

/**
 * 获取当前用户资料
 * @param {Object} session - 会话对象
 * @returns {Promise<Object|null>} 用户资料或null
 */
export const getCurrentUser = async (session) => {
  try {
    // 首先检查是否是快速体验模式
    const isExperienceMode = localStorage.getItem('experience_mode') === 'true';
    if (isExperienceMode) {
      // 检查是否已有体验用户信息
      const storedInfo = localStorage.getItem('user_info');
      if (storedInfo) {
        const parsedInfo = JSON.parse(storedInfo);
        if (parsedInfo.experienceUser) {
          // 确保体验用户有一个随机分配的ID
          if (!parsedInfo._id || parsedInfo._id === 'experience-user') {
            parsedInfo._id = generateExperienceUserId();
            localStorage.setItem('user_info', JSON.stringify(parsedInfo));
            localStorage.setItem('user_id', parsedInfo._id);
          }
          return parsedInfo;
        }
      }
      
      // 如果没有体验用户信息或信息不完整，创建新的体验用户信息
      const experienceUserId = generateExperienceUserId();
      const experienceUser = {
        nickname: '体验用户',
        experienceUser: true,
        created_at: new Date().toISOString(),
        _id: experienceUserId,
        phone: '13800138000', 
        isVip: true
      };
      
      // 存储到本地
      localStorage.setItem('user_info', JSON.stringify(experienceUser));
      localStorage.setItem('user_id', experienceUser._id);
      return experienceUser;
    }
    
    // 尝试从会话和数据库获取真实用户
    // 如果有会话，从会话中获取用户ID
    if (session?.user?.id) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('_id', session.user.id)
        .maybeSingle();
        
      if (error) throw error;
      
      if (data) {
        // 清除体验模式标志和体验用户ID，确保用户不再使用体验模式
        localStorage.removeItem('experience_mode');
        localStorage.removeItem('experience_user_id');
        // 将用户信息存储到本地存储
        localStorage.setItem('user_info', JSON.stringify(data));
        return data;
      }
    }
    
    // 如果没有会话，尝试从本地存储获取用户ID并查询数据库
    const userId = localStorage.getItem('user_id');
    if (userId && !userId.startsWith('exp-')) {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('_id', userId)
        .maybeSingle();
        
      if (error) throw error;
      
      if (data) {
        // 清除体验模式标志和体验用户ID
        localStorage.removeItem('experience_mode');
        localStorage.removeItem('experience_user_id');
        // 将用户信息存储到本地存储
        localStorage.setItem('user_info', JSON.stringify(data));
        return data;
      }
    }
    
    // 如果本地存储有用户信息，直接返回
    const storedUserInfo = localStorage.getItem('user_info');
    if (storedUserInfo) {
      const parsedInfo = JSON.parse(storedUserInfo);
      
      // 如果存储的是体验用户但体验模式已关闭，清除信息返回null
      if (parsedInfo.experienceUser && !isExperienceMode) {
        localStorage.removeItem('user_info');
        localStorage.removeItem('experience_user_id');
        return null;
      }
      
      return parsedInfo;
    }
    
    return null;
  } catch (error) {
    console.error('获取当前用户信息出错:', error);
    return null;
  }
};

/**
 * 根据ID获取用户资料
 * @param {string} userId - 用户ID
 * @returns {Promise<Object|null>} 用户资料或null
 */
export const getUserById = async (userId) => {
  try {
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('_id', userId)
      .maybeSingle();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('根据ID获取用户资料出错:', error);
    return null;
  }
};

/**
 * 创建新用户
 * @param {Object} userData - 用户数据
 * @returns {Promise<Object|null>} 创建的用户资料或null
 */
export const createUser = async (userData) => {
  try {
    // 确保有创建时间
    if (!userData.created_at) {
      userData.created_at = new Date().toISOString();
    }
    
    const { data, error } = await supabase
      .from('profiles')
      .insert([userData])
      .select()
      .single();
      
    if (error) throw error;
    
    // 存储到本地
    if (data) {
      localStorage.setItem('user_info', JSON.stringify(data));
    }
    
    return data;
  } catch (error) {
    console.error('创建用户出错:', error);
    return null;
  }
};

/**
 * 更新用户资料
 * @param {string} userId - 用户ID
 * @param {Object} updates - 更新数据
 * @returns {Promise<Object|null>} 更新后的用户资料或null
 */
export const updateUser = async (userId, updates) => {
  try {
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('_id', userId)
      .select()
      .single();
      
    if (error) throw error;
    
    // 更新本地存储的用户信息
    if (data) {
      const storedInfo = localStorage.getItem('user_info');
      if (storedInfo) {
        const parsedInfo = JSON.parse(storedInfo);
        localStorage.setItem('user_info', JSON.stringify({
          ...parsedInfo,
          ...updates
        }));
      } else {
        localStorage.setItem('user_info', JSON.stringify(data));
      }
    }
    
    return data;
  } catch (error) {
    console.error('更新用户资料出错:', error);
    return null;
  }
};

/**
 * 用户登出
 * @param {Object} session - 会话对象
 * @returns {Promise<void>}
 */
export const logoutUser = async (session) => {
  try {
    // 清除本地存储的所有用户相关数据
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_info');
    localStorage.removeItem('experience_mode');  // 清除体验模式标志
    localStorage.removeItem('experience_user_id'); // 清除体验用户ID
    
    // 如果有会话，注销
    if (session) {
      await supabase.auth.signOut();
    }
  } catch (error) {
    console.error('用户登出出错:', error);
    throw error;
  }
};

/**
 * 使用密码登录
 * @param {string} phone - 手机号
 * @param {string} hashedPassword - 经过哈希处理的密码
 * @returns {Promise<Object>} 登录结果
 */
export const loginWithPassword = async (phone, hashedPassword) => {
  try {
    // 查询用户资料
    const { data: user, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('phone', phone)
      .single();
    
    if (error) {
      return { success: false, error: '用户不存在' };
    }
    
    // 验证密码
    if (hashedPassword !== user.password) {
      return { success: false, error: '密码错误' };
    }
    
    return { success: true, user };
  } catch (error) {
    console.error('密码登录失败:', error);
    return { success: false, error: error.message || '登录失败' };
  }
};

/**
 * 检查用户是否存在
 * @param {string} phone - 手机号
 * @returns {Promise<Object>} 查询结果，包含用户是否存在
 */
export const checkUserExists = async (phone) => {
  try {
    // 查询用户是否存在
    const { data: user, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('phone', phone)
      .maybeSingle();
    
    if (error) {
      return { success: false, error: '查询用户信息失败' };
    }
    
    if (!user) {
      return { success: false, userExists: false, error: '该手机号未注册' };
    }
    
    return { success: true, userExists: true, user };
  } catch (error) {
    console.error('检查用户存在失败:', error);
    return { success: false, error: error.message || '查询用户失败' };
  }
};

/**
 * 保存用户登录状态
 * @param {Object} user - 用户信息
 * @returns {Object} 操作结果
 */
export const saveUserLoginState = (user) => {
  try {
    // 只有在非体验用户的情况下才清除体验模式标志
    if (!user.experienceUser) {
      localStorage.removeItem('experience_mode');
    }
    
    // 存储用户信息
    localStorage.setItem('user_id', user._id);
    localStorage.setItem('user_info', JSON.stringify(user));
    
    return { success: true };
  } catch (error) {
    console.error('保存用户登录状态失败:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 创建体验用户并保存登录状态
 * @returns {Object} 登录结果
 */
export const createExperienceUser = () => {
  try {
    // 设置体验模式标志
    localStorage.setItem('experience_mode', 'true');
    
    // 生成或获取体验用户的随机ID
    const experienceUserId = generateExperienceUserId();
    
    // 创建体验用户信息
    const experienceUser = {
      _id: experienceUserId,
      nickname: '体验用户',
      phone: '13800138000',
      isVip: true, // 让体验用户拥有VIP权限
      avatarUrl: '',
      experienceUser: true, // 标记为体验用户
      created_at: new Date().toISOString()
    };
    
    // 直接设置用户信息，而不是调用saveUserLoginState
    // 避免可能的冲突
    localStorage.setItem('user_id', experienceUser._id);
    localStorage.setItem('user_info', JSON.stringify(experienceUser));
    
    return { success: true, user: experienceUser };
  } catch (error) {
    console.error('创建体验用户失败:', error);
    return { success: false, error: error.message || '创建体验用户失败' };
  }
};

/**
 * 使用手机号和密码创建新用户
 * @param {string} phone - 手机号
 * @param {string} password - 已经过哈希处理的密码
 * @returns {Promise<Object>} 结果对象
 */
export const createNewUserWithPhone = async (phone, password) => {
  try {
    // 1. 检查用户是否已存在
    const { data: existingUser, error: existingUserError } = await supabase
      .from('profiles')
      .select('_id')
      .eq('phone', phone)
      .maybeSingle();

    if (existingUserError) throw existingUserError;
    if (existingUser) {
      return { success: false, error: '该手机号已注册' };
    }

    // 2. 创建用户资料 - 使用UUID生成唯一ID
    const userId = generateUUID();
    const { data, error: profileError } = await supabase
      .from('profiles')
      .insert([
        {
          _id: userId,
          phone,
          nickname: `用户${phone.substring(7)}`,
          avatarUrl: '',
          password // 传入的密码应该已经哈希处理
        }
      ])
      .select()
      .single();

    if (profileError) throw profileError;

    return { success: true, user: data };
  } catch (error) {
    console.error('创建新用户失败:', error);
    return { success: false, error: error.message || '注册失败' };
  }
};

/**
 * 更新用户的VIP会员状态
 * @param {string} userId - 用户ID
 * @param {boolean} isVip - VIP状态
 * @param {string} vipExpireDate - VIP过期日期
 * @returns {Promise<{success: boolean, data?: any, error?: any}>} - 更新结果
 */
export const updateVipStatus = async (userId, isVip, vipExpireDate) => {
  try {
    if (!userId) {
      return { success: false, error: '用户ID不能为空' };
    }

    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        isVip,
        vipExpireDate
      })
      .eq('_id', userId);

    if (error) {
      console.error('更新VIP状态失败:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('更新VIP状态异常:', error);
    return { success: false, error };
  }
};

/**
 * 检查VIP会员是否已过期
 * @param {string} vipExpireDate - VIP过期日期
 * @returns {boolean} - 是否已过期
 */
export const isVipExpired = (vipExpireDate) => {
  if (!vipExpireDate) return true;
  
  try {
    const expireDate = new Date(vipExpireDate);
    const now = new Date();
    return now > expireDate;
  } catch (error) {
    console.error('检查VIP过期状态异常:', error);
    return true;
  }
};

/**
 * 检查用户会话状态
 * @param {Object} session - 会话对象
 * @returns {Promise<boolean>} 是否已登录
 */
export const checkUserSession = async (session) => {
  try {
    // 首先检查本地存储中的用户ID，这是最快的检查方式
    const userId = localStorage.getItem('user_id');
    if (userId && userId !== 'experience-user') {
      return true;
    }
    
    // 检查是否在体验模式
    const isExperienceMode = localStorage.getItem('experience_mode') === 'true';
    if (isExperienceMode) {
      return true;
    }
    
    // 检查本地存储中是否有用户信息
    const storedUserInfo = localStorage.getItem('user_info');
    if (storedUserInfo) {
      return true;
    }
    
    // 只有当所有本地检查都失败时，才进行API调用获取用户信息
    if (session?.user?.id) {
      const { data } = await supabase
        .from('profiles')
        .select('_id')
        .eq('_id', session.user.id)
        .maybeSingle();
      
      return !!data;
    }
    
    return false;
  } catch (error) {
    console.error('检查用户会话状态出错:', error);
    return false;
  }
}; 